<template>
  <div>
    <Layout ref="layoutRef" :selectedOption="2" />
    <div class="mb-3">
      <h4>Atenciones por Hora y Día</h4>
    </div>
    <hr>
    <div class="mb-3 d-flex flex-wrap gap-2">
      <button
        v-for="atajo in atajos" :key="atajo.key"
        class="btn btn-sm" :class="atajo.clase"
        @click="aplicarAtajo(atajo)"
      >{{ atajo.label }}</button>
    </div>
    <div class="input-group mb-3">
      <span class="input-group-text">Desde</span>
      <input type="date" v-model="fechaDesde" class="form-control">
      <span class="input-group-text">Hasta</span>
      <input type="date" v-model="fechaHasta" class="form-control">
      <button
        class="btn btn-dark"
        @click="generarReporte"
        :disabled="!fechaDesde || !fechaHasta || cargando"
      >
        <span v-if="cargando" class="spinner-border spinner-border-sm me-1"></span>
        Consultar
      </button>
      <button
        class="btn btn-success"
        @click="exportToCSV"
        :disabled="!datos.length"
      >
        <span class="material-symbols-outlined align-middle" style="font-size:16px">download</span>
        CSV
      </button>
    </div>
    <div v-if="datos.length">
      <div class="row g-3 mb-4">
        <div class="col-6 col-md-3">
          <div class="kpi-card">
            <div class="kpi-label">Total atenciones</div>
            <div class="kpi-value">{{ totalAtenciones.toLocaleString() }}</div>
          </div>
        </div>
        <div class="col-6 col-md-3">
          <div class="kpi-card">
            <div class="kpi-label">Hora pico</div>
            <div class="kpi-value kpi-value--sm">{{ horaPico }}</div>
          </div>
        </div>
        <div class="col-6 col-md-3">
          <div class="kpi-card">
            <div class="kpi-label">Día más activo</div>
            <div class="kpi-value kpi-value--sm">{{ diaMasActivo }}</div>
          </div>
        </div>
        <div class="col-6 col-md-3">
          <div class="kpi-card">
            <div class="kpi-label">Promedio por hora</div>
            <div class="kpi-value">{{ promedioHora }}</div>
          </div>
        </div>
      </div>
      <div class="tabs">
        <button class="tablink" :class="{ 'active-tab': vistaActiva === 'heatmap' }" @click="vistaActiva = 'heatmap'">
          <span class="material-symbols-outlined" style="font-size:17px">grid_on</span>
          Heatmap
        </button>
        <button class="tablink" :class="{ 'active-tab': vistaActiva === 'barras' }" @click="vistaActiva = 'barras'">
          <span class="material-symbols-outlined" style="font-size:17px">bar_chart</span>
          Por hora
        </button>
        <button class="tablink" :class="{ 'active-tab': vistaActiva === 'dias' }" @click="vistaActiva = 'dias'">
          <span class="material-symbols-outlined" style="font-size:17px">calendar_month</span>
          Por día
        </button>
        <button class="tablink" :class="{ 'active-tab': vistaActiva === 'tabla' }" @click="vistaActiva = 'tabla'">
          <span class="material-symbols-outlined" style="font-size:17px">table_view</span>
          Tabla
        </button>
      </div>
      <!-- HEATMAP -->
      <div v-if="vistaActiva === 'heatmap'" class="mt-4">
        <p class="text-muted small mb-3">
          Intensidad de atenciones por hora del día y día de la semana. Más oscuro = más atenciones.
        </p>
        <div class="d-flex align-items-center gap-2 mb-3">
          <small class="text-muted">Menos</small>
          <div class="leyenda-gradiente"></div>
          <small class="text-muted">Más</small>
        </div>
        <div class="heatmap-wrapper">
          <div class="heatmap-grid" :style="gridStyle">
            <div class="heatmap-corner"></div>
            <div v-for="dia in DIAS" :key="dia" class="heatmap-dia-label">{{ dia }}</div>
            <template v-for="hora in horasActivas" :key="hora">
              <div class="heatmap-hora-label">{{ formatHora(hora) }}</div>
              <div
                v-for="dia in DIAS" :key="dia"
                class="heatmap-celda"
                :style="celdaStyle(hora, dia)"
                :title="`${dia} ${formatHora(hora)}: ${valorCelda(hora, dia)} atenciones`"
              >
                <span class="heatmap-celda__val">{{ valorCelda(hora, dia) || '' }}</span>
              </div>
            </template>
          </div>
        </div>
      </div>
      <!-- BARRAS POR HORA -->
      <div v-if="vistaActiva === 'barras'" class="mt-3">
        <apexchart type="bar" :options="chartHoraOptions" :series="chartHoraSeries" height="400" />
      </div>
      <!-- BARRAS POR DÍA -->
      <div v-if="vistaActiva === 'dias'" class="mt-3">
        <apexchart type="bar" :options="chartDiaOptions" :series="chartDiaSeries" height="400" />
      </div>
      <!-- TABLA -->
      <div v-if="vistaActiva === 'tabla'" class="mt-3">
        <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
          <div class="input-group input-group-sm" style="max-width:300px">
            <span class="input-group-text">
              <span class="material-symbols-outlined" style="font-size:15px">search</span>
            </span>
            <input
              type="text" class="form-control"
              placeholder="Buscar hora o día…"
              v-model="busqueda"
              @input="paginaActual = 1"
            >
            <button v-if="busqueda" class="btn btn-outline-secondary" @click="busqueda = ''; paginaActual = 1">
              <span class="material-symbols-outlined" style="font-size:15px">close</span>
            </button>
          </div>
          <div class="d-flex align-items-center gap-2">
            <small class="text-muted">Mostrar</small>
            <select class="form-select form-select-sm" style="width:75px" v-model="filasPorPagina" @change="paginaActual = 1">
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
            <small class="text-muted">filas</small>
          </div>
        </div>
        <table class="table table-bordered table-hover table-sm">
          <thead class="table-dark">
            <tr>
              <th class="sortable" @click="ordenar('dia')">
                Día
                <span class="material-symbols-outlined sort-icon">
                  {{ sortKey === 'dia' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}
                </span>
              </th>
              <th class="sortable" @click="ordenar('hora')">
                Hora
                <span class="material-symbols-outlined sort-icon">
                  {{ sortKey === 'hora' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}
                </span>
              </th>
              <th class="text-end sortable" @click="ordenar('cantidad')">
                Atenciones
                <span class="material-symbols-outlined sort-icon">
                  {{ sortKey === 'cantidad' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}
                </span>
              </th>
              <th class="text-end">%</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!filasPagina.length">
              <td colspan="4" class="text-center text-muted py-3">Sin resultados para "{{ busqueda }}"</td>
            </tr>
            <tr v-for="(f, i) in filasPagina" :key="i">
              <td>{{ f.dia }}</td>
              <td>{{ formatHora(f.hora) }}</td>
              <td class="text-end">{{ f.cantidad }}</td>
              <td class="text-end">
                <div class="d-flex align-items-center justify-content-end gap-2">
                  <div class="pct-bar-wrap">
                    <div class="pct-bar" :style="{ width: porcentaje(f.cantidad) + '%' }"></div>
                  </div>
                  {{ porcentaje(f.cantidad) }}%
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="table-secondary fw-bold">
              <td colspan="2">Total ({{ filasFiltradas.length }} registros{{ busqueda ? ' filtrados' : '' }})</td>
              <td class="text-end">{{ totalAtenciones }}</td>
              <td class="text-end">100%</td>
            </tr>
          </tfoot>
        </table>
        <div class="d-flex justify-content-between align-items-center">
          <small class="text-muted">Mostrando {{ rangoDesde }}–{{ rangoHasta }} de {{ filasFiltradas.length }} registros</small>
          <nav>
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" :class="{ disabled: paginaActual === 1 }">
                <button class="page-link" @click="paginaActual--">‹</button>
              </li>
              <li
                v-for="p in paginasVisibles" :key="p"
                class="page-item"
                :class="{ active: p === paginaActual, disabled: p === '…' }"
              >
                <button class="page-link" @click="p !== '…' && (paginaActual = p)">{{ p }}</button>
              </li>
              <li class="page-item" :class="{ disabled: paginaActual === totalPaginas }">
                <button class="page-link" @click="paginaActual++">›</button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
    <div v-else-if="consultado" class="alert alert-info mt-3">
      No hay datos para el período seleccionado.
    </div>
    <Footer />
  </div>
</template>

<script>
import Layout        from '@/components/Layout.vue'
import Footer        from '@/components/Footer.vue'
import VueApexCharts from 'vue3-apexcharts'
import api           from '@/services/api'
import {
  getToday, getThisWeek, getThisMonth,
  getThisQuarter, getThisSemester, getThisYear,
} from '@/modules/queryDateUtils.js'

const DIAS      = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']
const DIA_INDEX = { 1: 'Lun', 2: 'Mar', 3: 'Mié', 4: 'Jue', 5: 'Vie', 6: 'Sáb', 0: 'Dom' }

const HEAT_FROM = [219, 234, 254]
const HEAT_TO   = [30,  64,  175]

function lerp(a, b, t) { return Math.round(a + (b - a) * t) }
function heatColor(valor, maximo) {
  if (!maximo || !valor) return '#f1f5f9'
  const t = Math.min(valor / maximo, 1)
  return `rgb(${lerp(HEAT_FROM[0], HEAT_TO[0], t)},${lerp(HEAT_FROM[1], HEAT_TO[1], t)},${lerp(HEAT_FROM[2], HEAT_TO[2], t)})`
}

/**
 * Extrae el día de semana desde fechaCita.
 * El string viene con offset -05:00, p.ej. "2024-03-11T00:00:00.000-05:00".
 * Parseamos solo la parte de fecha (YYYY-MM-DD) para evitar que
 * la conversión UTC desplace el día.
 */
function diaDeSemana(fechaCita) {
  // Tomamos únicamente "2024-03-11" ignorando hora y offset
  const solofecha = fechaCita.substring(0, 10)          // "2024-03-11"
  const [y, m, d] = solofecha.split('-').map(Number)
  const date = new Date(y, m - 1, d)                    // fecha local sin UTC shift
  return DIA_INDEX[date.getDay()]
}

/**
 * Extrae la hora entera desde horaDesde.
 * horaDesde viene como "09:00" o "09:00:00".
 */
function horaEntera(horaDesde) {
  return parseInt(horaDesde.split(':')[0], 10)
}

export default {
  name: 'ReporteAtencionesPorHoraDia',
  components: { Layout, Footer, apexchart: VueApexCharts },
  data() {
    return {
      fechaDesde:     '',
      fechaHasta:     '',
      datos:          [],   // VwAtencion[]
      vistaActiva:    'heatmap',
      cargando:       false,
      consultado:     false,
      busqueda:       '',
      filasPorPagina: 25,
      paginaActual:   1,
      sortKey:        'cantidad',
      sortDir:        'desc',
      DIAS,
      atajos: [
        { key: 'hoy',       label: 'Hoy',             clase: 'btn-outline-primary',   fn: getToday        },
        { key: 'semana',    label: 'Semana actual',    clase: 'btn-outline-danger',    fn: getThisWeek     },
        { key: 'mes',       label: 'Mes actual',       clase: 'btn-outline-secondary', fn: getThisMonth    },
        { key: 'trimestre', label: 'Trimestre actual', clase: 'btn-outline-success',   fn: getThisQuarter  },
        { key: 'semestre',  label: 'Semestre actual',  clase: 'btn-outline-info',      fn: getThisSemester },
        { key: 'anio',      label: 'Año actual',       clase: 'btn-outline-warning',   fn: getThisYear     },
      ],
    }
  },
  computed: {
    // ── Núcleo: agrupa VwAtencion[] → { dia, hora, cantidad }[]
    filasPlanas() {
      const mapa = {}
      this.datos.forEach((atencion) => {
        const { fechaCita, horaDesde } = atencion
        if (!fechaCita || !horaDesde) return

        const dia  = diaDeSemana(fechaCita)
        const hora = horaEntera(horaDesde)

        if (!dia && dia !== 0) return   // guarda por si DIA_INDEX devuelve undefined

        const key = `${dia}_${hora}`
        if (!mapa[key]) mapa[key] = { dia, hora, cantidad: 0 }
        mapa[key].cantidad += 1         // cada VwAtencion = 1 atención
      })
      return Object.values(mapa)
    },

    mapaHeatmap() {
      const m = {}
      this.filasPlanas.forEach(({ dia, hora, cantidad }) => {
        if (!m[hora]) m[hora] = {}
        m[hora][dia] = (m[hora][dia] ?? 0) + cantidad
      })
      return m
    },
    horasActivas() {
      return [...new Set(this.filasPlanas.map(f => f.hora))].sort((a, b) => a - b)
    },
    maxCelda() {
      return Math.max(...this.filasPlanas.map(f => f.cantidad), 1)
    },
    totalAtenciones() {
      return this.datos.length   // total real = número de registros
    },
    horaPico() {
      if (!this.filasPlanas.length) return '—'
      const porHora = {}
      this.filasPlanas.forEach(({ hora, cantidad }) => {
        porHora[hora] = (porHora[hora] ?? 0) + cantidad
      })
      const hora = Object.entries(porHora).sort((a, b) => b[1] - a[1])[0][0]
      return this.formatHora(Number(hora))
    },
    diaMasActivo() {
      if (!this.filasPlanas.length) return '—'
      const porDia = {}
      this.filasPlanas.forEach(({ dia, cantidad }) => {
        porDia[dia] = (porDia[dia] ?? 0) + cantidad
      })
      return Object.entries(porDia).sort((a, b) => b[1] - a[1])[0][0]
    },
    promedioHora() {
      if (!this.horasActivas.length) return 0
      return (this.totalAtenciones / this.horasActivas.length).toFixed(1)
    },
    gridStyle() {
      return { gridTemplateColumns: `64px repeat(${DIAS.length}, 1fr)` }
    },

    // ── Gráfico por hora ──
    chartHoraOptions() {
      const porHora = {}
      this.filasPlanas.forEach(({ hora, cantidad }) => {
        porHora[hora] = (porHora[hora] ?? 0) + cantidad
      })
      const horas = Object.keys(porHora).map(Number).sort((a, b) => a - b)
      return {
        chart:       { toolbar: { show: false }, fontFamily: 'inherit' },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
        colors:      ['#3b82f6'],
        dataLabels:  { enabled: false },
        xaxis: {
          categories: horas.map(h => this.formatHora(h)),
          title: { text: 'Hora del día', style: { fontWeight: 600 } },
        },
        yaxis: { title: { text: 'Atenciones', style: { fontWeight: 600 } } },
        tooltip: { y: { formatter: v => `${v.toLocaleString()} atenciones` } },
        grid: { borderColor: '#e9ecef' },
      }
    },
    chartHoraSeries() {
      const porHora = {}
      this.filasPlanas.forEach(({ hora, cantidad }) => {
        porHora[hora] = (porHora[hora] ?? 0) + cantidad
      })
      const horas = Object.keys(porHora).map(Number).sort((a, b) => a - b)
      return [{ name: 'Atenciones', data: horas.map(h => porHora[h]) }]
    },

    // ── Gráfico por día ──
    chartDiaOptions() {
      return {
        chart:       { toolbar: { show: false }, fontFamily: 'inherit' },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
        colors:      ['#10b981'],
        dataLabels:  { enabled: false },
        xaxis: {
          categories: DIAS,
          title: { text: 'Día de la semana', style: { fontWeight: 600 } },
        },
        yaxis: { title: { text: 'Atenciones', style: { fontWeight: 600 } } },
        tooltip: { y: { formatter: v => `${v.toLocaleString()} atenciones` } },
        grid: { borderColor: '#e9ecef' },
      }
    },
    chartDiaSeries() {
      const porDia = {}
      DIAS.forEach(d => { porDia[d] = 0 })
      this.filasPlanas.forEach(({ dia, cantidad }) => {
        porDia[dia] = (porDia[dia] ?? 0) + cantidad
      })
      return [{ name: 'Atenciones', data: DIAS.map(d => porDia[d]) }]
    },

    // ── Tabla ──
    filasFiltradas() {
      const q = this.busqueda.toLowerCase().trim()
      const base = q
        ? this.filasPlanas.filter(f =>
            f.dia.toLowerCase().includes(q) ||
            this.formatHora(f.hora).includes(q)
          )
        : [...this.filasPlanas]
      base.sort((a, b) => {
        const av = a[this.sortKey]
        const bv = b[this.sortKey]
        if (av < bv) return this.sortDir === 'asc' ? -1 : 1
        if (av > bv) return this.sortDir === 'asc' ? 1 : -1
        return 0
      })
      return base
    },
    totalPaginas() {
      return Math.max(1, Math.ceil(this.filasFiltradas.length / this.filasPorPagina))
    },
    rangoDesde() {
      return this.filasFiltradas.length ? (this.paginaActual - 1) * this.filasPorPagina + 1 : 0
    },
    rangoHasta() {
      return Math.min(this.paginaActual * this.filasPorPagina, this.filasFiltradas.length)
    },
    filasPagina() {
      return this.filasFiltradas.slice(this.rangoDesde - 1, this.rangoHasta)
    },
    paginasVisibles() {
      const total = this.totalPaginas, cur = this.paginaActual
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
      const pages = [1]
      if (cur > 3) pages.push('…')
      for (let p = Math.max(2, cur - 1); p <= Math.min(total - 1, cur + 1); p++) pages.push(p)
      if (cur < total - 2) pages.push('…')
      pages.push(total)
      return pages
    },
  },
  mounted() {
    this.$refs.layoutRef.toggleProcesos()
    this.$refs.layoutRef.toggleReportes()
    this.$refs.layoutRef.toggleParametros()
    this.$refs.layoutRef.toggleAyuda()
  },
  methods: {
    aplicarAtajo(atajo) {
      const { fechaDesde, fechaHasta } = atajo.fn()
      this.fechaDesde = fechaDesde
      this.fechaHasta = fechaHasta
      this.generarReporte()
    },
    async generarReporte() {
      if (!this.fechaDesde || !this.fechaHasta) return
      this.cargando     = true
      this.consultado   = false
      this.busqueda     = ''
      this.paginaActual = 1
      try {
        const idEmpresa = window.sessionStorage.getItem('idEmpresa')
        const { data }  = await api.get('/vwatencions/por-fecha', {
          params: { idEmpresa, fechaDesde: this.fechaDesde, fechaHasta: this.fechaHasta },
        })
        this.datos = data ?? []
      } catch (error) {
        console.error('Error al obtener los datos:', error)
        this.datos = []
      } finally {
        this.cargando   = false
        this.consultado = true
      }
    },
    formatHora(hora) {
      return `${String(hora).padStart(2, '0')}:00`
    },
    valorCelda(hora, dia) {
      return this.mapaHeatmap[hora]?.[dia] ?? 0
    },
    celdaStyle(hora, dia) {
      const valor = this.valorCelda(hora, dia)
      const bg    = heatColor(valor, this.maxCelda)
      const t     = valor / this.maxCelda
      return {
        backgroundColor: bg,
        color: t > 0.5 ? '#fff' : '#1e293b',
      }
    },
    porcentaje(valor) {
      if (!this.totalAtenciones) return 0
      return ((valor / this.totalAtenciones) * 100).toFixed(1)
    },
    ordenar(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortDir = key === 'cantidad' ? 'desc' : 'asc'
      }
      this.paginaActual = 1
    },
    exportToCSV() {
      const filas = [
        ['Día', 'Hora', 'Atenciones', '%'],
        ...this.filasPlanas
          .sort((a, b) => {
            const di = DIAS.indexOf(a.dia) - DIAS.indexOf(b.dia)
            return di !== 0 ? di : a.hora - b.hora
          })
          .map(f => [
            f.dia,
            this.formatHora(f.hora),
            f.cantidad,
            `${this.porcentaje(f.cantidad)}%`,
          ]),
        ['Total', '', this.totalAtenciones, '100%'],
      ]
      const csv  = filas.map(f => f.join(';')).join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url  = URL.createObjectURL(blob)
      const a    = Object.assign(document.createElement('a'), {
        href: url, download: 'Reporte_Atenciones_Por_Hora_Dia.csv',
      })
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
  },
}
</script>

<style scoped>
/* ── KPIs ── */
.kpi-card {
  background: #f8f9fa; border: 1px solid #dee2e6;
  border-radius: 8px; padding: 14px 18px; text-align: center;
}
.kpi-label {
  font-size: 0.75rem; color: #6c757d;
  text-transform: uppercase; letter-spacing: .04em; margin-bottom: 4px;
}
.kpi-value     { font-size: 1.6rem; font-weight: 700; color: #0d6efd; line-height: 1.1; }
.kpi-value--sm { font-size: 1rem;   font-weight: 700; color: #0d6efd; }
/* ── Tabs ── */
.tabs { display: flex; gap: 4px; border-bottom: 2px solid #dee2e6; }
.tablink {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 18px; border: none; border-bottom: 3px solid transparent;
  margin-bottom: -2px; background: transparent; color: #6c757d;
  font-size: 0.875rem; font-weight: 500; cursor: pointer;
  border-radius: 6px 6px 0 0; transition: all 0.2s ease;
}
.tablink:hover { color: #0d6efd; background: #f0f4ff; }
.active-tab    { color: #0d6efd; border-bottom-color: #0d6efd; background: #fff; font-weight: 600; }
/* ── Heatmap ── */
.heatmap-wrapper { overflow-x: auto; }
.heatmap-grid    { display: grid; gap: 3px; min-width: 520px; }
.heatmap-corner  { /* vacío */ }
.heatmap-dia-label {
  text-align: center; font-size: 0.72rem; font-weight: 700;
  color: #475569; padding: 4px 0; text-transform: uppercase; letter-spacing: .04em;
}
.heatmap-hora-label {
  font-size: 0.72rem; color: #64748b;
  display: flex; align-items: center; justify-content: flex-end;
  padding-right: 8px; white-space: nowrap;
}
.heatmap-celda {
  border-radius: 5px; min-height: 36px;
  display: flex; align-items: center; justify-content: center;
  transition: opacity 0.2s; cursor: default;
}
.heatmap-celda:hover { opacity: 0.82; }
.heatmap-celda__val  { font-size: 0.68rem; font-weight: 700; line-height: 1; }
.leyenda-gradiente   {
  width: 120px; height: 10px; border-radius: 4px;
  background: linear-gradient(to right, #dbeafe, #1e40af);
}
/* ── Tabla ── */
.sortable        { cursor: pointer; user-select: none; white-space: nowrap; }
.sortable:hover  { background: #495057; }
.sort-icon       { font-size: 14px; vertical-align: middle; opacity: .7; }
.pct-bar-wrap    { width: 60px; height: 6px; background: #e9ecef; border-radius: 3px; overflow: hidden; }
.pct-bar         { height: 100%; background: #0d6efd; border-radius: 3px; transition: width 0.4s ease; }
</style>